// Dependencies
import React, {Component} from "react";
import {connect} from "react-redux";
import {withRouter, RouteComponentProps} from "react-router-dom";
import {Components} from "@webtech/webtech-ui-react/build/webtech-ui";
import Moment from "moment";
import _ from "lodash";
import DatePicker from "react-datepicker";
import {SubsidiaryUtils} from "../../../../utils/Subsidiary/SubsidiaryUtils";
import {forceDownload} from "../../../../utils/utils";

// Store
import {ActionCreator} from "../../../../actions/actions";
import {vppsStoreSelectors} from "../../store/store";
import {VppsActionCreators} from "../../actions/actionCreators";
import {CreationModes} from "../../reducers/creationMode.reducer";

// Elements
import Paginator from "../../../../utils/Paginator/Paginator.component";
import SearchUsers from "../SearchUsers/SearchUsers.component";
import Drawer from "./../../../../utils/Drawer/Drawer.component";
import CreateVpp from "./../CreateVpp/CreateVpp.component";
import SubsidiaryFilter from "../SubsidiaryFilter.component";
import {Dialog} from "../../../../utils/Dialog/Dialog.component";

// Utils
import {checkPermission} from "../../../../utils/Authorization/authorization";

// Styles
import "./VppsList.component.scss";
import "react-datepicker/dist/react-datepicker.css";

// Constants
const LOGS_PER_PAGE = 20;

export interface VppsListProps extends RouteComponentProps {
    getVpps: ActionCreator;
    getVppsUsers: ActionCreator;
    updateCreationMode: ActionCreator;
    clearCreateVpp: ActionCreator;
    getSubsidiaries: ActionCreator;
    submitVpp: ActionCreator;
    deleteVpp: ActionCreator;
    subsidiaryList: any[];
    creationMode: any;
    vpps: {
        results: any[];
        count: number;
    };
    vppsLoading: boolean;
    vppsUsers: {
        results: any[];
        count: number;
    };
    currentVppsUsers: {
        results: any[];
        count: number;
    };
    vppsUsersLoading: boolean;
}

export interface VppsListState {
    selectedString: string;
    selectedUsers: any[];
    selectedSubsidiaries: string[];
    currentPage: number;
    selectedRange: {
        start: string;
        end: string;
    };
    showDialog: boolean;
    vppToDelete: any;
}

const usersSearchHeader = (isLoading, onSearch) => {
    return (
        <SearchUsers
            key={"search-field"}
            onSearch={onSearch}
            isLoading={isLoading}
        />
    );
};

const usersItems = users => {
    let items = [];

    if (users.length > 0) {
        users.map(user => {
            items.push({
                label: (
                    <div>
                        {user.first_name} {user.last_name}
                    </div>
                ),
                value: user.id
            });
        });
    }

    return items;
};

class VppsList extends Component<VppsListProps, VppsListState> {
    userFilter: React.RefObject<Components.SelectMenu>;
    rangeStartFilter: React.RefObject<DatePicker>;
    rangeEndFilter: React.RefObject<DatePicker>;
    subsidiaryFilter;

    constructor(props) {
        super(props);
        this.userFilter = React.createRef();
        this.rangeStartFilter = React.createRef();
        this.subsidiaryFilter = React.createRef();
        this.rangeEndFilter = React.createRef();

        const start = null;
        const end = null;

        this.state = {
            selectedString: "",
            selectedUsers: [],
            selectedSubsidiaries: [],
            currentPage: 1,
            selectedRange: {
                start,
                end
            },
            showDialog: false,
            vppToDelete: null
        };

        this.props.getSubsidiaries();
        this.getVpps()
    }

    getVpps() {
        this.props.getVpps({
            page: this.state.currentPage,
            top: LOGS_PER_PAGE,
            search: this.state.selectedString,
            subsidiaries: this.state.selectedSubsidiaries,
            users: this.state.selectedUsers.map(user => user.id),
            range: this.state.selectedRange
        });
    }

    getUsersToList = () => {
        let users =
            this.props.vppsUsers.results.length == 0
                ? [...this.props.currentVppsUsers.results]
                : [...this.props.vppsUsers.results];
        if (this.state.selectedUsers.length > 0)
            users = users.concat(this.state.selectedUsers);
        return _.uniqBy(users, "id");
    };

    selectSearch = e => {
        this.setState({
            selectedString: e.target.value,
            currentPage: 1
        }, this.getVpps);
    };

    selectUsers = e => {
        const users = _.filter(this.getUsersToList(), user => {
            return e.includes(user.id);
        });

        this.setState({
            selectedUsers: users,
            currentPage: 1
        }, this.getVpps);
    };

    setStartDate = e => {
        let selectedRange = {...this.state.selectedRange};
        const start = Moment(e);
        selectedRange.start = start.startOf("day").toISOString();

        if (start > Moment(this.state.selectedRange.end)) {
            selectedRange.end = start.endOf("day").toISOString();
        }

        this.setState({
            selectedRange,
            currentPage: 1
        }, this.getVpps);
    };

    setEndDate = e => {
        let selectedRange = {...this.state.selectedRange};
        const end = Moment(e);
        selectedRange.end = end.endOf("day").toISOString();

        if (end < Moment(this.state.selectedRange.start)) {
            selectedRange.start = end.startOf("day").toISOString();
        }

        this.setState({
            selectedRange,
            currentPage: 1
        }, this.getVpps);
    };

    onUserSearch = search => {
        if (search.length > 3) {
            this.props.getVppsUsers(search);
        } else {
            this.props.getVppsUsers(null);
        }
    };

    onFilterSubsidiaries = subsidiaries => {
        this.setState({
            selectedSubsidiaries: subsidiaries,
            currentPage: 1
        }, this.getVpps);
    };

    onClearFilters = () => {
        this.userFilter.current.selectItem([]);
        this.userFilter.current.setDisplayString("Filter By Users...");
        this.subsidiaryFilter.current.clearFilter();

        const start = null;
        const end = null;

        if (this.userFilter.current) {
            this.userFilter.current.setState({
                id: _.uniqueId()
            });
        }

        this.setState({
            selectedString: "",
            selectedUsers: [],
            currentPage: 1,
            selectedRange: {
                start,
                end
            }
        });
    };

    pageChange = newPage => {
        this.setState({
            currentPage: newPage
        }, this.getVpps);
    };

    renderVppRows = () => {
        return (
            <tbody>
            {this.props.vpps.results.map((vpp, key) => {
                const subsidiary = this.props.subsidiaryList.find(
                    sub => sub.id == vpp.subsidiary_id
                );
                return (
                    <tr key={key}>
                        <td className="vpp-user-image">
                            {(!vpp.user.profile_picture_b64 || vpp.user.profile_picture_b64 == "") && (
                                <div className="user-icon">
                                    <i className="icon-user-light"/>
                                </div>
                            )}
                            {vpp.user.profile_picture_b64 && vpp.user.profile_picture_b64 !== "" && (
                                <div
                                    style={{
                                        backgroundImage: vpp.user.profile_picture_b64.includes('data:image/png;base64')
                                            ? `url('${vpp.user.profile_picture_b64}')`
                                            : `url('data:image;base64,${vpp.user.profile_picture_b64}')`
                                    }}
                                />
                            )}
                        </td>
                        <td className="vpp-user">
                            {vpp.user ?
                                <div>
                                    <p>
                                        {`${vpp.user.first_name} ${vpp.user.last_name}`}
                                    </p>
                                </div>
                                : "-"
                            }
                        </td>
                        <td>
                            <div>
                                <p>{vpp.name}</p>
                            </div>
                        </td>
                        <td className="vpp-subsidiary">
                            <div>
                                {subsidiary ? SubsidiaryUtils.subsidiaryIcon(subsidiary) : <p>-</p>}
                            </div>
                        </td>
                        <td className="vpp-date">
                            {Moment(new Date(vpp.timestamp)).format(
                                "DD MMM YYYY"
                            )}
                        </td>
                        <td className="vpp-download-cell">
                            <div
                                className="download-vpp"
                                onClick={() =>
                                    forceDownload(
                                        `${window.location.protocol}//${window.location.hostname}/wave/panelvideos/${vpp.upload}/${vpp.name}.mp4`,
                                        `${vpp.name}.mp4`
                                    )
                                }
                            >
                                <i className="icon-play-circle-light"></i>
                                .MP4
                            </div>
                        </td>
                        <td className="vpp-download-cell">
                            <div
                                className="download-vpp"
                                onClick={() =>
                                    forceDownload(
                                        `${window.location.protocol}//${window.location.hostname}/wave/panelvideos/${vpp.upload}/${vpp.name}.vpp`,
                                        `${vpp.name}.vpp`
                                    )
                                }
                            >
                                <i className="icon-file-archive-light"></i>
                                .VPP
                            </div>
                        </td>
                        <td>
                            {checkPermission("delete_vpp") && (
                                <button
                                    className="delete-vpp tooltip-icon"
                                    onClick={() => this.onDeleteVpp(vpp.id)}
                                >
                                    <span className="tooltip-text">Delete</span>
                                    <i className="icon-trash-alt-light"></i>
                                </button>
                            )}
                        </td>
                    </tr>
                );
            })}
            </tbody>
        );
    };

    renderVpps = () => {
        if (this.props.vppsLoading) {
            return (
                <div className="col-12 vpp-list-loading">
                    <Components.LoadingRipple
                        width={"100px"}
                        height={"100px"}
                    />
                </div>
            );
        } else if (this.props.vpps.results.length === 0) {
            return (
                <div className="vpps-list-no-vpp">
                    No Videos found to display. Please select different values
                    to try again.
                </div>
            );
        } else {
            return (
                <React.Fragment>
                    <div className="vpps-container">
                        <table>
                            <thead>
                            <tr>
                                <td colSpan={2}>Users</td>
                                <td>Videos</td>
                                <td className="vpp-subsidiary">
                                    Subsidiary
                                </td>
                                <td className="vpp-date">Uploaded On</td>
                                <td className="vpp-download-cell">
                                    <div>
                                        <p>Download Original</p>
                                    </div>
                                </td>
                                <td className="vpp-download-cell">
                                    <div>
                                        <p>Download Vpp</p>
                                    </div>
                                </td>
                                <td></td>
                            </tr>
                            </thead>
                            {this.renderVppRows()}
                        </table>
                    </div>
                    <div className="paginator-col">
                        <Paginator
                            currentPage={this.state.currentPage}
                            totalPages={
                                Math.floor(this.props.vpps.count / 24) + 1
                            }
                            onPageChange={this.pageChange}
                        />
                    </div>
                </React.Fragment>
            );
        }
    };

    createVpp = () => {
        this.props.updateCreationMode(CreationModes.ADD_VPP);
    };

    submitVpp = vpp => {
        this.props.submitVpp(vpp, {
            page: this.state.currentPage,
            top: LOGS_PER_PAGE,
            search: this.state.selectedString,
            subsidiaries: this.state.selectedSubsidiaries,
            users: this.state.selectedUsers.map(user => user.id),
            range: this.state.selectedRange
        });
    };

    closeCreateVpp = () => {
        this.props.clearCreateVpp();
    };

    onDeleteVpp = vppToDelete => {
        this.setState({
            showDialog: true,
            vppToDelete
        });
    };

    onDialogClose = () => {
        this.setState({
            showDialog: false,
            vppToDelete: null
        });
    };

    onDialogConfirm = () => {
        this.props.deleteVpp(this.state.vppToDelete, {
            page: this.state.currentPage,
            top: LOGS_PER_PAGE,
            search: this.state.selectedString,
            subsidiaries: this.state.selectedSubsidiaries,
            users: this.state.selectedUsers.map(user => user.id),
            range: this.state.selectedRange
        });

        this.setState({
            showDialog: false,
            vppToDelete: null
        });
    };

    renderFilters = () => {
        const range = {
            start: this.state.selectedRange.start
                ? new Date(this.state.selectedRange.start)
                : null,
            end: this.state.selectedRange.end
                ? new Date(this.state.selectedRange.end)
                : null
        };

        const userItems = usersItems(this.getUsersToList());
        return (
            <div id="VppsPageFiltersBox">
                <div className="row">
                    <div className="adjustable-filters">
                        <div className="vpps-filter">
                            <label className="label">Search</label>
                            <input
                                className="input"
                                placeholder="Video Name"
                                onChange={this.selectSearch}
                            />
                        </div>

                        <div className="vpps-filter">
                            <label className="label">Users</label>
                            <Components.SelectMenu
                                ref={this.userFilter}
                                items={userItems}
                                noValueLabel="Filter By Users..."
                                multipleSelection
                                headerContainer={usersSearchHeader(
                                    this.props.vppsUsersLoading,
                                    this.onUserSearch
                                )}
                                value={
                                    this.state.selectedUsers.length > 0
                                        ? this.state.selectedUsers.map(
                                            user => user.id
                                        )
                                        : null
                                }
                                onChange={this.selectUsers}
                            />
                        </div>

                        <div className="vpps-filter">
                            <label className="label">Subsidiaries</label>
                            <SubsidiaryFilter
                                filter={this.state.selectedSubsidiaries}
                                ref={this.subsidiaryFilter}
                                onFilterSubsidiary={this.onFilterSubsidiaries}
                            />
                        </div>

                        <div className="vpps-filter">
                            <label className="label">Date Range</label>
                            <div className="date-range">
                                <DatePicker
                                    selected={range.start}
                                    ref={this.rangeStartFilter}
                                    onChange={this.setStartDate}
                                    selectsStart
                                    startDate={range.start}
                                    endDate={range.end}
                                />
                                <div className="range-icon">
                                    <i className="icon-arrow-right-light"/>
                                </div>
                                <DatePicker
                                    selected={range.end}
                                    ref={this.rangeEndFilter}
                                    onChange={this.setEndDate}
                                    selectsEnd
                                    startDate={range.start}
                                    endDate={range.end}
                                    minDate={range.start}
                                />
                            </div>
                        </div>

                        <div>
                            <button
                                className="clear-filters-btn"
                                onClick={this.onClearFilters}
                            >
                                Clear All Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const isLoading = this.props.vppsLoading;

        return (
            <div id="VppsList" className="content">
                <div className="container">
                    {checkPermission("create_vpp") && (
                        <div className="row">
                            <div className="col-12 center-vertical buttons-col">
                                <a
                                    className="new-vpp red-btn"
                                    onClick={this.createVpp}
                                >
                                    <span>New Custom VPP</span>
                                </a>
                            </div>
                        </div>
                    )}
                    <div className="row">
                        <div className="col-12">
                            <div className="vpps-filters">
                                {this.renderFilters()}
                            </div>
                        </div>
                    </div>

                    <div className="vpp-list">
                        {isLoading ? (
                            <div className="vpp-list-loading">
                                <Components.LoadingRipple
                                    width={"100px"}
                                    height={"100px"}
                                />
                            </div>
                        ) : (
                            <div className="vpps row">{this.renderVpps()}</div>
                        )}
                    </div>

                    {this.props.creationMode == CreationModes.ADD_VPP && (
                        <Drawer
                            title="New Custom VPP"
                            onClose={this.closeCreateVpp}
                        >
                            <CreateVpp createVpp={this.submitVpp}/>
                        </Drawer>
                    )}
                </div>

                <Dialog
                    open={this.state.showDialog}
                    onConfirm={this.onDialogConfirm}
                    onClose={this.onDialogClose}
                >
                    You are about to delete a Video. Are you sure?
                </Dialog>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    vpps: vppsStoreSelectors.vpps(state),
    vppsLoading: vppsStoreSelectors.vppsLoading(state),
    vppsUsers: vppsStoreSelectors.vppsUsers(state),
    vppsUsersLoading: vppsStoreSelectors.vppsUsersLoading(state),
    currentVppsUsers: vppsStoreSelectors.currentVppsUsers(state),
    creationMode: vppsStoreSelectors.creationMode(state),
    subsidiaryList: vppsStoreSelectors.subsidiaryList(state)
});

const mapDispatchToProps = {
    getVpps: VppsActionCreators.getVpps,
    getVppsUsers: VppsActionCreators.getVppsUsers,
    updateCreationMode: VppsActionCreators.updateCreationMode,
    clearCreateVpp: VppsActionCreators.clearCreateVpp,
    getSubsidiaries: VppsActionCreators.getSubsidiaries,
    submitVpp: VppsActionCreators.submitVpp,
    deleteVpp: VppsActionCreators.deleteVpp
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withRouter(VppsList));
